import { useCallback, useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Loader2, LogOut } from "lucide-react";
import { portalApi, getToken, clearSession } from "./api";

export default function PortalLayout() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  const load = useCallback(() => {
    portalApi("/client/auth/me")
      .then((data) => {
        setMe(data);
        setLoading(false);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!getToken()) return;
    load();
  }, [load]);

  if (!getToken()) return <Navigate to="/portal/login" replace />;

  if (loading || !me) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-parchment-base" data-testid="portal-loading">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-700" />
      </main>
    );
  }

  const logout = () => {
    clearSession();
    navigate("/portal/login");
  };

  const isAdmin = me.user.account_type === "client_admin";
  const linkCls = ({ isActive }) =>
    `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-emerald-500/20 text-emerald-200" : "text-emerald-100/60 hover:text-emerald-100"}`;

  return (
    <div className="min-h-screen bg-parchment-base" data-testid="portal-layout">
      <header className="noise-overlay sticky top-0 z-40 bg-forest-deep shadow-lg shadow-emerald-950/20">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3 rounded-xl bg-[#F1EFE8] px-3 py-1.5">
            <img src="/logo-mark.png" alt="CLYMACO" className="h-6 w-auto" />
          </div>
          <div className="min-w-0">
            <div className="truncate font-display text-sm font-bold text-[#FBF9F4]" data-testid="portal-org-name">{me.org.legal_name}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-400/80">EPR Control Portal</div>
          </div>
          <nav className="mx-auto flex items-center gap-1" data-testid="portal-nav">
            <NavLink to="/portal" end className={linkCls} data-testid="portal-nav-home">Home</NavLink>
            <NavLink to="/portal/network" className={linkCls} data-testid="portal-nav-network">Network</NavLink>
            {isAdmin && (
              <NavLink to="/portal/team" className={linkCls} data-testid="portal-nav-team">Team</NavLink>
            )}
            <NavLink to="/portal/account" className={linkCls} data-testid="portal-nav-account">Account</NavLink>
          </nav>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-emerald-100/60 sm:block" data-testid="portal-user-name">{me.user.name}</span>
            <button onClick={logout} data-testid="portal-logout" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-semibold text-emerald-100 transition-colors hover:bg-white/10">
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
      </header>
      <Outlet context={{ me, reload: load }} />
    </div>
  );
}
